/// Running in Circles — main application logic

// ─── Config ─────────────────────────────────────────────────────────

// OSRM runs locally on temoserver — no API key, no limits
// nginx proxies /osrm/* to the osrm container
const OSRM_URL = '/osrm';

// Nominatim is free but rate-limited (1 req/sec). No key needed.
const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

// CartoDB Voyager tiles (free, nice aesthetics)
const CARTO_TILES = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png';
const CARTO_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

// ─── State ──────────────────────────────────────────────────────────

let map = null;
let marker = null;
let routeLayer = null;
let waypointMarkers = [];
let currentLocation = null; // { lat, lon, label }
let currentRouteCoords = null; // last displayed route coords [lat, lon][]
let currentRouteWaypoints = null; // last route's OSRM waypoints (for Google Maps link)
let currentRouteName = null; // name of the current route (for imported/shared/saved routes)

// ─── Route Editing State ───────────────────────────────────────────
let editNodes = [];          // [{ lat, lon, marker }] — draggable control points
let editHistory = [];        // stack of previous editNodes states (snapshots for undo)
let isEditing = false;        // true when edit mode is active
let hasUnsavedEdits = false;  // true when route has been modified since last save
let isReRouting = false;      // true while an OSRM re-route is in progress
let previewLayer = null;      // temporary polyline shown during node drag
let ghostMarker = null;       // temporary marker at click position before snap

// Voice guidance state
let guidanceActive = false;
let guidanceWatchId = null;
let guidanceTurns = []; // extracted turn points from route
let guidanceUserMarker = null;
let guidanceAccuracyCircle = null;
let guidanceWakeLock = null;
let guidanceLastSpeed = 0;

// ─── Map Setup ──────────────────────────────────────────────────────

function initMap() {
  map = L.map('map', {
    zoomControl: true,
    attributionControl: true,
    doubleClickZoom: false, // we use double-click for adding nodes
  }).setView([60.1699, 24.9384], 13); // Helsinki default

  L.tileLayer(CARTO_TILES, {
    attribution: CARTO_ATTR,
    subdomains: 'abcd',
    maxZoom: 19,
  }).addTo(map);

  // Double-click: add new node (only in edit mode)
  map.on('dblclick', onMapDblClick);
}

// ─── UI Elements ────────────────────────────────────────────────────

const $address = document.getElementById('address');
const $geocodeBtn = document.getElementById('geocode-btn');
const $gpsBtn = document.getElementById('gps-btn');
const $locationDisplay = document.getElementById('location-display');
const $distance = document.getElementById('distance');
const $distanceValue = document.getElementById('distance-value');
const $profile = document.getElementById('profile');
const $generateBtn = document.getElementById('generate-btn');
const $status = document.getElementById('status');
const $routeInfo = document.getElementById('route-info');
const $routeDistance = document.getElementById('route-distance');
const $routeTime = document.getElementById('route-time');
const $routePoints = document.getElementById('route-points');
const $routeTurns = document.getElementById('route-turns');

// Additional options elements
const $optionsHeader = document.getElementById('options-header');
const $optionsPanel = document.getElementById('options-panel');
const $optSurface = document.getElementById('opt-surface');
const $optMinTurns = document.getElementById('opt-min-turns');
const $optMinCrossings = document.getElementById('opt-min-crossings');
const $optMinTrafficLights = document.getElementById('opt-min-traffic-lights');
const $optAvoidMainRoads = document.getElementById('opt-avoid-main-roads');
const $optPreferLit = document.getElementById('opt-prefer-lit');
const $optScenic = document.getElementById('opt-scenic');

// ─── UI Helpers ─────────────────────────────────────────────────────

function showStatus(msg, type = 'loading') {
  $status.className = `status visible ${type}`;
  $status.innerHTML = type === 'loading'
    ? `<span class="spinner"></span>${msg}`
    : msg;
}

function hideStatus() {
  $status.className = 'status';
  $status.innerHTML = '';
}

function showError(msg) {
  showStatus(msg, 'error');
}

function updateLocationDisplay(lat, lon, label) {
  $locationDisplay.classList.add('active');
  $locationDisplay.innerHTML = `
    <div class="label">${label || 'Location'}</div>
    <div class="coords">${lat.toFixed(5)}, ${lon.toFixed(5)}</div>
  `;
}

function setLocation(lat, lon, label) {
  currentLocation = { lat, lon, label };

  // Update display
  updateLocationDisplay(lat, lon, label);

  // Update map
  if (marker) map.removeLayer(marker);
  marker = L.marker([lat, lon]).addTo(map);
  marker.bindPopup(label || 'Start/End point').openPopup();
  map.setView([lat, lon], 14);

  // Enable generate button
  $generateBtn.disabled = false;
}

// ─── Geocoding (Nominatim) ──────────────────────────────────────────

async function geocodeAddress(address) {
  showStatus('Searching for address...');

  try {
    const url = `${NOMINATIM_URL}?q=${encodeURIComponent(address)}&format=json&limit=1`;
    const res = await fetch(url, {
      headers: { 'Accept-Language': 'en' },
    });

    if (!res.ok) throw new Error(`Geocoding failed: ${res.status}`);

    const data = await res.json();
    if (!data.length) {
      showError(`No results found for "${address}"`);
      return;
    }

    const result = data[0];
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);
    const label = result.display_name;

    setLocation(lat, lon, label);
    hideStatus();
  } catch (err) {
    showError(`Search error: ${err.message}`);
  }
}

// ─── Device GPS ─────────────────────────────────────────────────────

function useDeviceLocation() {
  showStatus('Getting your location...');

  if (!navigator.geolocation) {
    showError('Geolocation not supported by your browser');
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const { latitude, longitude } = pos.coords;
      setLocation(latitude, longitude, 'Your device location');
      hideStatus();
    },
    (err) => {
      let msg = 'Could not get your location';
      if (err.code === 1) msg = 'Location access denied. Check your browser permissions.';
      if (err.code === 2) msg = 'Location unavailable. Try entering an address instead.';
      if (err.code === 3) msg = 'Location request timed out. Try again.';
      showError(msg);
    },
    { enableHighAccuracy: true, timeout: 10000 }
  );
}

// ─── Waypoint Generation (Seed-Based Polygon) ──────────────────────

/**
 * Generate polygon vertices around a start point for route generation.
 *
 * Instead of placing many waypoints evenly on a circle, we place 3-4
 * vertices forming a rough polygon (triangle or diamond). This reduces
 * snap distortion, dead-ends, and gives simpler, more recognizable loops.
 *
 * The start point is the CENTER of the polygon, not a vertex. OSRM /trip
 * with source=first starts at the start point, visits all vertices, and
 * returns via destination=any.
 *
 * @param {number} lat - Start latitude (polygon center)
 * @param {number} lon - Start longitude (polygon center)
 * @param {number} radiusMeters - Base radius for the polygon
 * @param {number} numVertices - 3 (triangle) or 4 (diamond)
 * @param {number} heading - Initial heading in radians (orientation)
 * @param {number[]} shapeVariation - Per-vertex scale factors (default [1.0, ...])
 * @returns {Array<{lat, lon, angle, radius}>} Polygon vertices
 */
function generatePolygonWaypoints(lat, lon, radiusMeters, numVertices, heading, shapeVariation) {
  const R = 6371000; // Earth radius in meters
  const waypoints = [];

  for (let i = 0; i < numVertices; i++) {
    const angle = heading + (i / numVertices) * 2 * Math.PI;
    const r = radiusMeters * (shapeVariation[i] ?? 1.0);

    // Offset in meters (cos=east-west, sin=north-south)
    const dx = r * Math.cos(angle);
    const dy = r * Math.sin(angle);

    // Convert meter offsets to lat/lon
    const deltaLat = (dy / R) * (180 / Math.PI);
    const deltaLon = (dx / R) * (180 / Math.PI) / Math.cos(lat * Math.PI / 180);

    waypoints.push({
      lat: lat + deltaLat,
      lon: lon + deltaLon,
      angle,
      radius: r,
    });
  }

  return waypoints;
}

/**
 * Calculate the actual distance of a route from its GeoJSON coordinates.
 */
function calculateRouteDistance(coords) {
  let dist = 0;
  for (let i = 1; i < coords.length; i++) {
    const [lat1, lon1] = coords[i - 1];
    const [lat2, lon2] = coords[i];
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(lat1 * Math.PI / 180) *
              Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) ** 2;
    dist += 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  return dist;
}

/**
 * Count the number of meaningful turns in a route.
 *
 * A "turn" is a point where the heading changes by more than `threshold` degrees
 * (default 25°). This counts distinct intersections where you need to change
 * direction — the number a runner needs to memorize.
 *
 * We also merge consecutive coords that are very close together (< 5m) to
 * avoid counting GPS noise as turns.
 */
function countTurns(coords, thresholdDeg = 25) {
  if (coords.length < 3) return 0;

  // Merge points closer than 5m to reduce noise
  const merged = [coords[0]];
  for (let i = 1; i < coords.length; i++) {
    const prev = merged[merged.length - 1];
    const dLat = (coords[i][0] - prev[0]) * 111000;
    const dLon = (coords[i][1] - prev[1]) * 111000 * Math.cos(prev[0] * Math.PI / 180);
    if (Math.hypot(dLat, dLon) > 5) {
      merged.push(coords[i]);
    }
  }

  let turns = 0;
  let prevHeading = null;

  for (let i = 1; i < merged.length; i++) {
    const [lat1, lon1] = merged[i - 1];
    const [lat2, lon2] = merged[i];
    const dLat = lat2 - lat1;
    const dLon = lon2 - lon1;
    // Skip zero-length segments
    if (dLat === 0 && dLon === 0) continue;

    const heading = Math.atan2(dLon, dLat) * 180 / Math.PI;

    if (prevHeading !== null) {
      let delta = Math.abs(heading - prevHeading);
      if (delta > 180) delta = 360 - delta;

      if (delta > thresholdDeg) {
        turns++;
      }
    }
    prevHeading = heading;
  }

  return turns;
}

// ─── Scenic Route Generation (Approach 2) ──────────────────────────

/**
 * Overpass API endpoints for querying OSM scenic nodes.
 * Tried in order — first available is used.
 */
const OVERPASS_ENDPOINTS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
];

/**
 * In-memory cache for Overpass queries, keyed by grid cell.
 * Avoids re-querying the same area when generating multiple routes nearby.
 */
const overpassCache = new Map();

function overpassCacheKey(lat, lon, radiusM) {
  // ~2km grid cells
  const grid = 0.02;
  return `${Math.round(lat / grid)}_${Math.round(lon / grid)}_${Math.round(radiusM / 1000)}`;
}

/**
 * Scenic score by node type. Higher = more scenic/preferable.
 */
const SCENIC_SCORES = {
  'park_large':       1.0,
  'park_small':       0.7,
  'nature_reserve':   1.0,
  'forest':           0.8,
  'wood':             0.7,
  'water':            0.9,
  'beach':            0.9,
  'viewpoint':        0.8,
  'monument':         0.5,
};

/**
 * Categorize a scenic node by its OSM tags.
 * @returns {string|null} category key for SCENIC_SCORES
 */
function categorizeScenicNode(tags) {
  if (!tags) return null;

  // Nature reserves
  if (tags.leisure === 'nature_reserve' || tags.boundary === 'national_park')
    return 'nature_reserve';

  // Parks — large if has area tag > 5ha, or name suggests it
  if (tags.leisure === 'park' || tags.leisure === 'garden' || tags.leisure === 'green') {
    return tags['area:ha'] && parseFloat(tags['area:ha']) > 5 ? 'park_large' : 'park_small';
  }

  // Forests and woods (common in Finland)
  if (tags.landuse === 'forest') return 'forest';
  if (tags.natural === 'wood') return 'wood';

  // Water features
  if (tags.natural === 'water' || tags.natural === 'lake') return 'water';
  if (tags.natural === 'beach') return 'beach';
  if (tags.waterway === 'river' || tags.waterway === 'stream') return 'water';

  // Viewpoints and monuments
  if (tags.tourism === 'viewpoint') return 'viewpoint';
  if (tags.historic === 'monument') return 'monument';

  return null;
}

/**
 * Get the scenic score for a node.
 */
function scenicScore(node) {
  return SCENIC_SCORES[node.type] ?? 0.5;
}

/**
 * Query the Overpass API for scenic nodes (parks, water, forests, viewpoints)
 * within a bounding box around the start point.
 *
 * @returns {Array<{lat, lon, distance, type, tags, name}>} scenic nodes
 */
async function findScenicNodes(lat, lon, targetDistanceMeters) {
  // Search within 40% of target distance
  const radius = targetDistanceMeters * 0.4;
  const cacheKey = overpassCacheKey(lat, lon, radius);
  if (overpassCache.has(cacheKey)) return overpassCache.get(cacheKey);

  // Build bounding box
  const latDelta = radius / 111000;
  const lonDelta = radius / (111000 * Math.cos(lat * Math.PI / 180));
  const bbox = `${lat - latDelta},${lon - lonDelta},${lat + latDelta},${lon + lonDelta}`;

  // Build Overpass QL query
  const query = `[out:json][timeout:10];
    (
      node["leisure"~"park|garden|green|nature_reserve"](${bbox});
      way["leisure"~"park|garden|green|nature_reserve"](${bbox});
      node["landuse"~"grass|meadow|recreation_ground|forest"](${bbox});
      way["landuse"~"grass|meadow|recreation_ground|forest"](${bbox});
      node["natural"~"water|wood|beach"](${bbox});
      way["natural"~"water|wood|beach"](${bbox});
      way["waterway"~"river|stream"](${bbox});
      node["tourism"="viewpoint"](${bbox});
      node["historic"="monument"](${bbox});
    );
    out center 200;`;

  for (const endpoint of OVERPASS_ENDPOINTS) {
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: 'data=' + encodeURIComponent(query),
      });

      if (!res.ok) continue;

      const data = await res.json();
      const nodes = parseScenicNodes(data, lat, lon);

      // Cache results
      overpassCache.set(cacheKey, nodes);
      return nodes;
    } catch (err) {
      continue; // try next endpoint
    }
  }

  return []; // no results or all endpoints failed
}

/**
 * Parse Overpass API response into scenic node objects.
 */
function parseScenicNodes(overpassData, startLat, startLon) {
  const nodes = [];

  if (!overpassData?.elements) return nodes;

  for (const el of overpassData.elements) {
    // For ways/relations, use center coords; for nodes, use lat/lon
    const elLat = el.lat ?? el.center?.lat;
    const elLon = el.lon ?? el.center?.lon;
    if (elLat == null || elLon == null) continue;

    const tags = el.tags || {};
    const type = categorizeScenicNode(tags);
    if (!type) continue;

    const distance = haversine(startLat, startLon, elLat, elLon);

    // Skip nodes that are basically at the start point
    if (distance < 100) continue;

    nodes.push({
      lat: elLat,
      lon: elLon,
      distance,
      type,
      tags,
      name: tags.name || tags['name:en'] || null,
      id: el.id,
    });
  }

  // Sort by scenic score descending, then by distance
  nodes.sort((a, b) => scenicScore(b) - scenicScore(a) || a.distance - b.distance);

  return nodes;
}

/**
 * Select scenic hubs that are spread around the compass and have good scenic scores.
 *
 * Greedy angular spread: pick highest-scoring node first, then pick subsequent
 * nodes that maximize compass spread while maintaining scenic quality.
 *
 * @param {Array} scenicNodes - all discovered scenic nodes
 * @param {number} startLat, startLon - start point
 * @param {number} targetDistanceMeters - target route distance
 * @param {number} numHubs - 2 or 3 hubs to select
 * @returns {Array} selected hub nodes
 */
function selectScenicHubs(scenicNodes, startLat, startLon, targetDistanceMeters, numHubs) {
  if (scenicNodes.length === 0) return [];

  const idealHubDist = targetDistanceMeters / (numHubs + 1);
  const minDist = idealHubDist * 0.3;
  const maxDist = idealHubDist * 1.8;

  // Filter by distance range
  let candidates = scenicNodes.filter(n => n.distance >= minDist && n.distance <= maxDist);
  if (candidates.length < numHubs) {
    // Relax distance constraints
    candidates = scenicNodes.filter(n => n.distance <= maxDist * 1.5);
  }
  if (candidates.length === 0) return [];

  // Sort by scenic score descending
  candidates.sort((a, b) => scenicScore(b) - scenicScore(a));

  const selected = [];
  const selectedAngles = [];

  // Pick first hub: highest scenic score
  selected.push(candidates[0]);
  selectedAngles.push(bearing(startLat, startLon, candidates[0].lat, candidates[0].lon));
  candidates = candidates.slice(1);

  // Pick remaining hubs: maximize angular spread + scenic score
  while (selected.length < numHubs && candidates.length > 0) {
    let bestIdx = 0;
    let bestScore = -Infinity;

    for (let i = 0; i < candidates.length; i++) {
      const angle = bearing(startLat, startLon, candidates[i].lat, candidates[i].lon);

      // Minimum angular distance to any already-selected hub
      let minAngularDist = Infinity;
      for (const selAngle of selectedAngles) {
        let diff = Math.abs(angle - selAngle);
        if (diff > 180) diff = 360 - diff;
        minAngularDist = Math.min(minAngularDist, diff);
      }

      // Score: scenic quality * angular spread weight
      const idealSpread = 360 / (numHubs + 1);
      const angularWeight = Math.min(minAngularDist / idealSpread, 1.0);
      const score = scenicScore(candidates[i]) * (0.3 + 0.7 * angularWeight);

      if (score > bestScore) {
        bestScore = score;
        bestIdx = i;
      }
    }

    selected.push(candidates[bestIdx]);
    selectedAngles.push(bearing(startLat, startLon, candidates[bestIdx].lat, candidates[bestIdx].lon));
    candidates.splice(bestIdx, 1);
  }

  return selected;
}

/**
 * Generate multiple hub combinations for refinement.
 *
 * Since scenic hubs are fixed points (can't nudge a park), we iterate by
 * trying different hub combinations from the pool of scenic nodes.
 *
 * @returns {Array<Array>} array of hub sets to try
 */
function generateHubCombinations(scenicNodes, startLat, startLon, targetDistanceMeters, numHubs, maxCombinations) {
  const combinations = [];

  // Strategy 1: Best scenic score (primary selection)
  const primary = selectScenicHubs(scenicNodes, startLat, startLon, targetDistanceMeters, numHubs);
  if (primary.length === numHubs) combinations.push(primary);

  // Strategy 2: Closer hubs (shorter route)
  const idealHubDist = targetDistanceMeters / (numHubs + 1);
  const closerNodes = scenicNodes.filter(n => n.distance < idealHubDist * 0.8);
  if (closerNodes.length >= numHubs) {
    const closer = selectScenicHubs(closerNodes, startLat, startLon, targetDistanceMeters * 0.8, numHubs);
    if (closer.length === numHubs && !hubSetsEqual(combinations[0], closer)) {
      combinations.push(closer);
    }
  }

  // Strategy 3: Farther hubs (longer route)
  const fartherNodes = scenicNodes.filter(n => n.distance > idealHubDist * 1.0);
  if (fartherNodes.length >= numHubs) {
    const farther = selectScenicHubs(fartherNodes, startLat, startLon, targetDistanceMeters * 1.2, numHubs);
    if (farther.length === numHubs && !hubSetsEqual(combinations[0], farther)) {
      combinations.push(farther);
    }
  }

  // Strategy 4: Random combinations from top-scoring nodes
  const topNodes = scenicNodes.slice(0, Math.min(10, scenicNodes.length));
  while (combinations.length < maxCombinations && topNodes.length >= numHubs) {
    const combo = randomHubCombo(topNodes, startLat, startLon, numHubs);
    if (combo && combo.length === numHubs && !combinations.some(c => hubSetsEqual(c, combo))) {
      combinations.push(combo);
    } else {
      break; // avoid infinite loop if we can't find unique combos
    }
  }

  return combinations.slice(0, maxCombinations);
}

/**
 * Check if two hub sets contain the same nodes (by id).
 */
function hubSetsEqual(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  const idsA = a.map(h => h.id).sort();
  const idsB = b.map(h => h.id).sort();
  return idsA.every((id, i) => id === idsB[i]);
}

/**
 * Pick a random combination of hubs with angular spread.
 */
function randomHubCombo(nodes, startLat, startLon, numHubs) {
  if (nodes.length < numHubs) return null;

  // Shuffle and pick first N that have decent angular spread
  const shuffled = [...nodes].sort(() => Math.random() - 0.5);
  const selected = [];
  const selectedAngles = [];

  for (const node of shuffled) {
    if (selected.length >= numHubs) break;

    const angle = bearing(startLat, startLon, node.lat, node.lon);

    // Check angular distance to already-selected
    let minAngularDist = Infinity;
    for (const selAngle of selectedAngles) {
      let diff = Math.abs(angle - selAngle);
      if (diff > 180) diff = 360 - diff;
      minAngularDist = Math.min(minAngularDist, diff);
    }

    // Require at least 60° between hubs
    if (selected.length === 0 || minAngularDist > 60) {
      selected.push(node);
      selectedAngles.push(angle);
    }
  }

  return selected.length === numHubs ? selected : null;
}

/**
 * Route through a set of scenic hubs using OSRM /trip.
 * Returns the same result shape as refinePolygonRoute.
 */
async function routeThroughHubs(startLat, startLon, hubs, profileParam, fetchOptions) {
  const coords = [
    `${startLon},${startLat}`,
    ...hubs.map(h => `${h.lon},${h.lat}`),
  ].join(';');

  const url = `${OSRM_URL}/trip/v1/${profileParam}/${coords}` +
    `?roundtrip=true&source=first&destination=any&geometries=geojson&overview=full`;

  try {
    const res = await fetch(url, fetchOptions);
    if (!res.ok) return null;
    const data = await res.json();
    if (data.code !== 'Ok' || !data.trips?.length) return null;

    const trip = data.trips[0];
    const routeCoords = trip.geometry.coordinates.map(([lon, lat]) => [lat, lon]);
    const trimmed = trimDeadEnds(routeCoords);
    const actualDistance = calculateRouteDistance(trimmed);

    return {
      trip,
      osrmWaypoints: data.waypoints,
      distance: actualDistance,
      turnCount: countTurns(trimmed),
      routeCoords: trimmed,
    };
  } catch (err) {
    return null;
  }
}

/**
 * Generate a scenic route by discovering scenic nodes (parks, water, forests)
 * via the Overpass API, selecting hubs, and trying different hub combinations
 * to converge on the target distance.
 *
 * Falls back to the geometric polygon approach (Approach 1) if no scenic
 * nodes are found or Overpass is unavailable.
 *
 * @returns { result, scenicHubs } or { result, scenicHubs: null } on fallback
 */
async function generateScenicRoute(startLat, startLon, targetDistanceMeters,
                                   profileParam, fetchOptions, wantMinTurns) {
  const distanceKm = targetDistanceMeters / 1000;
  const numHubs = distanceKm <= 5 ? 2 : 3;
  const tolerance = distanceKm > 15 ? 0.15 : 0.10;
  const maxCombinations = wantMinTurns ? 6 : 4;

  // Step 1: Query Overpass for scenic nodes
  showStatus('Finding scenic spots near you... 🌳');
  const scenicNodes = await findScenicNodes(startLat, startLon, targetDistanceMeters);

  if (scenicNodes.length < numHubs) {
    // Not enough scenic nodes — fall back to Approach 1
    return { result: null, scenicHubs: null, fallback: true };
  }

  // Step 2: Generate hub combinations
  const hubSets = generateHubCombinations(
    scenicNodes, startLat, startLon, targetDistanceMeters, numHubs, maxCombinations
  );

  if (hubSets.length === 0) {
    return { result: null, scenicHubs: null, fallback: true };
  }

  // Step 3: Route through each hub set and pick the best
  let bestResult = null;
  let bestScore = Infinity;
  let bestHubs = null;
  let comboNum = 0;

  for (const hubSet of hubSets) {
    comboNum++;
    showStatus(`Generating scenic route (${comboNum}/${hubSets.length})...`);

    const result = await routeThroughHubs(startLat, startLon, hubSet, profileParam, fetchOptions);
    if (!result) continue;

    const rawDistError = (result.distance - targetDistanceMeters) / targetDistanceMeters;
    const distError = Math.abs(rawDistError);
    if (distError > 0.4) continue; // hard reject

    // Directional bias: penalize short routes more than long ones.
    const directionalError = rawDistError < 0
      ? distError * 1.5   // being short is worse
      : distError * 0.8;  // being long is more acceptable

    // Score: distance accuracy + scenic quality bonus
    const scenicBonus = hubSet.reduce((sum, h) => sum + scenicScore(h), 0);
    const score = wantMinTurns
      ? result.turnCount * 10 + directionalError * 100 - scenicBonus * 5
      : directionalError * 100 - scenicBonus * 5 + result.turnCount * 0.01;

    if (score < bestScore) {
      bestScore = score;
      bestResult = result;
      bestHubs = hubSet;
    }

    // Early exit if within tolerance
    if (distError <= tolerance && !wantMinTurns) break;
  }

  if (!bestResult) {
    return { result: null, scenicHubs: null, fallback: true };
  }

  return { result: bestResult, scenicHubs: bestHubs, fallback: false };
}

// ─── GPX Import ────────────────────────────────────────────────────

/**
 * Parse a GPX 1.1 file and extract track coordinates.
 * Handles <trk><trkseg><trkpt> structure.
 * @returns { coords: [[lat,lon],...], name: string|null, distance: number, time: string|null }
 */
function parseGPX(gpxText) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(gpxText, 'text/xml');

  const parseError = doc.querySelector('parsererror');
  if (parseError) throw new Error('Invalid GPX file');

  let name = null;
  const nameEl = doc.querySelector('metadata > name') || doc.querySelector('trk > name');
  if (nameEl) name = nameEl.textContent.trim();

  let time = null;
  const timeEl = doc.querySelector('metadata > time') || doc.querySelector('trk > time');
  if (timeEl) time = timeEl.textContent.trim();

  const trkpts = doc.querySelectorAll('trk > trkseg > trkpt, trk > trkseg trkpt');
  if (trkpts.length < 2) throw new Error('GPX file has too few track points');

  const coords = [];
  for (const pt of trkpts) {
    const lat = parseFloat(pt.getAttribute('lat'));
    const lon = parseFloat(pt.getAttribute('lon'));
    if (!isNaN(lat) && !isNaN(lon)) {
      coords.push([lat, lon]);
    }
  }

  if (coords.length < 2) throw new Error('No valid track points found in GPX');

  const distance = calculateRouteDistance(coords);

  return { coords, name, distance, time };
}

/**
 * Handle GPX file import from file input.
 */
async function handleGPXImport(file) {
  showStatus('📂 Importing GPX...');

  try {
    const text = await file.text();
    const parsed = parseGPX(text);

    currentRouteName = parsed.name;

    displayImportedRoute(parsed.coords, parsed.name, parsed.distance);

    const distKm = (parsed.distance / 1000).toFixed(2);
    showStatus(`Imported: ${parsed.name || 'GPX route'} · ${distKm} km`, 'success');
    setTimeout(() => hideStatus(), 4000);

  } catch (err) {
    showError(`GPX import failed: ${err.message}`);
  }
}

/**
 * Display an imported or shared route on the map.
 * Similar to displayRoute() but works with raw coords (no OSRM trip data).
 */
function displayImportedRoute(coords, name, distance) {
  // Exit previous edit mode
  exitEditMode();

  if (routeLayer) map.removeLayer(routeLayer);
  waypointMarkers.forEach(m => map.removeLayer(m));
  waypointMarkers = [];

  currentRouteCoords = coords;
  currentRouteWaypoints = null;

  routeLayer = L.polyline(coords, {
    color: '#e94560',
    weight: 5,
    opacity: 0.8,
  }).addTo(map);

  // Click on route line: insert a new node
  routeLayer.on('click', onRouteClick);

  map.fitBounds(routeLayer.getBounds(), { padding: [40, 40] });

  const distKm = (distance / 1000).toFixed(2);
  const profile = $profile?.value || 'foot';
  const timeMin = Math.round((distance / 1000) / (profile === 'bike' ? 4.5 : 1.4) * 60);
  const timeStr = timeMin >= 60
    ? `${Math.floor(timeMin / 60)}h ${timeMin % 60}min`
    : `${timeMin} min`;

  $routeDistance.textContent = `${distKm} km`;
  $routeTime.textContent = timeStr;
  $routePoints.textContent = name || 'Imported route';

  const turns = countTurns(coords);
  $routeTurns.textContent = turns === 0
    ? 'Straight loop — no turns!'
    : `${turns} turn${turns > 1 ? 's' : ''}`;

  $routeInfo.classList.add('visible');

  const $scenicRow = document.getElementById('route-scenic-row');
  if ($scenicRow) $scenicRow.style.display = 'none';

  // GPX export only (no Google Maps without waypoints)
  const $exportGpx = document.getElementById('export-gpx');
  const $exportGmaps = document.getElementById('export-gmaps');
  const $infoGpx = document.getElementById('info-gpx');
  const $infoGmaps = document.getElementById('info-gmaps');
  if ($exportGpx) $exportGpx.style.display = 'flex';
  if ($exportGmaps) $exportGmaps.style.display = 'none';
  if ($infoGpx) $infoGpx.style.display = 'flex';
  if ($infoGmaps) $infoGmaps.style.display = 'none';

  // Show guidance + save + share buttons
  const $guidanceBtn = document.getElementById('guidance-btn');
  const $saveBtn = document.getElementById('save-route-btn');
  const $shareBtn = document.getElementById('share-route-btn');
  if ($guidanceBtn) $guidanceBtn.style.display = 'flex';
  if ($saveBtn) $saveBtn.style.display = 'flex';
  if ($shareBtn) $shareBtn.style.display = 'flex';

  // Enter edit mode
  enterEditMode();
}

// ─── Polyline Encoding for URL Sharing ─────────────────────────────

/**
 * Encode coordinates into a compact polyline string (Google polyline format).
 * Reduces precision to 5 decimal places (~1.1m) for URL efficiency.
 */
function encodePolyline(coords) {
  const precision = 5;
  const factor = Math.pow(10, precision);

  let result = '';
  let prevLat = 0, prevLon = 0;

  for (const [lat, lon] of coords) {
    const encLat = Math.round(lat * factor);
    const encLon = Math.round(lon * factor);

    const dLat = encLat - prevLat;
    const dLon = encLon - prevLon;

    result += encodeSignedNumber(dLat) + encodeSignedNumber(dLon);

    prevLat = encLat;
    prevLon = encLon;
  }

  return result;
}

function encodeSignedNumber(num) {
  let sgnNum = num << 1;
  if (num < 0) sgnNum = ~sgnNum;
  return encodeUnsignedNumber(sgnNum);
}

function encodeUnsignedNumber(num) {
  let str = '';
  while (num >= 0x20) {
    str += String.fromCharCode((0x20 | (num & 0x1f)) + 63);
    num >>= 5;
  }
  str += String.fromCharCode(num + 63);
  return str;
}

/**
 * Decode a polyline string back into coordinates.
 */
function decodePolyline(str) {
  const precision = 5;
  const factor = Math.pow(10, precision);

  const coords = [];
  let index = 0, lat = 0, lon = 0;

  while (index < str.length) {
    lat += decodeSignedNumber(str, index);
    index = _lastDecodeIndex;
    lon += decodeSignedNumber(str, index);
    index = _lastDecodeIndex;

    coords.push([lat / factor, lon / factor]);
  }

  return coords;
}

let _lastDecodeIndex = 0;

function decodeSignedNumber(str, start) {
  let result = 0, shift = 0, b;
  let index = start;

  do {
    b = str.charCodeAt(index++) - 63;
    result |= (b & 0x1f) << shift;
    shift += 5;
  } while (b >= 0x20);

  const value = (result & 1) ? ~(result >> 1) : (result >> 1);
  _lastDecodeIndex = index;
  return value;
}

/**
 * Generate a shareable URL for the current route.
 * Format: #r=<polyline>&n=<urlencoded_name>
 */
function generateShareLink() {
  if (!currentRouteCoords || currentRouteCoords.length < 2) return null;

  const encoded = encodePolyline(currentRouteCoords);
  const params = new URLSearchParams();
  params.set('r', encoded);
  if (currentRouteName) params.set('n', currentRouteName);

  return window.location.origin + window.location.pathname + '#' + params.toString();
}

/**
 * Parse the URL hash for a shared route on page load.
 * @returns { coords, name } or null
 */
function parseShareLinkFromURL() {
  if (!window.location.hash) return null;

  const hash = window.location.hash.slice(1);
  const params = new URLSearchParams(hash);

  const encoded = params.get('r');
  if (!encoded) return null;

  try {
    const coords = decodePolyline(encoded);
    if (coords.length < 2) return null;

    const name = params.get('n') || 'Shared route';
    return { coords, name };
  } catch (err) {
    return null;
  }
}

/**
 * Open the share modal with the shareable link.
 */
function openShareModal() {
  const link = generateShareLink();
  const $linkInput = document.getElementById('share-link-input');
  if ($linkInput) {
    $linkInput.value = link || 'Unable to generate link';
    $linkInput.select();
  }
  openModal('modal-share');
}

// ─── Route Library (localStorage) ──────────────────────────────────

const ROUTE_STORAGE_KEY = 'running-in-circles-routes';

function getSavedRoutes() {
  try {
    const raw = localStorage.getItem(ROUTE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function setSavedRoutes(routes) {
  localStorage.setItem(ROUTE_STORAGE_KEY, JSON.stringify(routes));
}

function saveCurrentRoute() {
  if (!currentRouteCoords || currentRouteCoords.length < 2) return;

  const routes = getSavedRoutes();
  const name = currentRouteName || `Route ${routes.length + 1}`;
  const distance = calculateRouteDistance(currentRouteCoords);

  const route = {
    id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
    name,
    distance,
    coords: currentRouteCoords,
    created: new Date().toISOString(),
  };

  routes.unshift(route);
  if (routes.length > 50) routes.length = 50;

  setSavedRoutes(routes);
  renderSavedRoutes();

  hasUnsavedEdits = false;

  showStatus(`Saved: ${name}`, 'success');
  setTimeout(() => hideStatus(), 3000);
}

function deleteSavedRoute(id) {
  const routes = getSavedRoutes().filter(r => r.id !== id);
  setSavedRoutes(routes);
  renderSavedRoutes();
}

function loadSavedRoute(id) {
  const route = getSavedRoutes().find(r => r.id === id);
  if (!route) return;

  currentRouteName = route.name;
  displayImportedRoute(route.coords, route.name, route.distance);
}

function renderSavedRoutes() {
  const routes = getSavedRoutes();
  const $panel = document.getElementById('saved-routes-panel');
  const $list = document.getElementById('saved-routes-list');

  if (!$panel || !$list) return;

  if (routes.length === 0) {
    $panel.style.display = 'none';
    return;
  }

  $panel.style.display = 'block';
  $list.innerHTML = '';

  for (const route of routes) {
    const item = document.createElement('div');
    item.className = 'saved-route-item';
    item.innerHTML = `
      <span class="route-name">${escapeHtml(route.name)}</span>
      <span class="route-meta">${(route.distance / 1000).toFixed(1)} km</span>
      <button class="route-delete" data-id="${route.id}" title="Delete">×</button>
    `;

    item.addEventListener('click', (e) => {
      if (e.target.classList.contains('route-delete')) return;
      loadSavedRoute(route.id);
    });

    const delBtn = item.querySelector('.route-delete');
    if (delBtn) {
      delBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        deleteSavedRoute(route.id);
      });
    }

    $list.appendChild(item);
  }
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// ─── Route Editing (Interactive Map) ───────────────────────────────

/**
 * Maximum number of editable control nodes.
 * Prevents excessive OSRM calls and overly complex routes.
 */
const MAX_EDIT_NODES = 12;

/**
 * Snapshot the current editNodes state for undo.
 * Stores only lat/lon (not markers — markers are recreated on restore).
 */
function pushEditHistory() {
  const snapshot = editNodes.map(n => ({ lat: n.lat, lon: n.lon }));
  editHistory.push(snapshot);
  updateUndoButton();
}

/**
 * Restore the previous edit state from history.
 */
function undoLastEdit() {
  if (editHistory.length === 0) return;

  const prev = editHistory.pop();

  // Remove current node markers
  editNodes.forEach(n => n.marker?.remove());
  editNodes = prev.map(p => ({
    lat: p.lat,
    lon: p.lon,
    marker: null,
  }));

  // Redraw node markers at restored positions
  drawEditNodes();

  // Re-route through restored nodes
  reRouteThroughNodes();
  updateUndoButton();
}

function updateUndoButton() {
  const $undoBtn = document.getElementById('edit-undo-btn');
  if ($undoBtn) $undoBtn.disabled = editHistory.length === 0;
}

/**
 * Enter edit mode: make the route line interactive and show the edit toolbar.
 * Called after any route is displayed (generated, imported, loaded, shared).
 */
function enterEditMode() {
  if (isEditing) return;
  isEditing = true;

  // Initialize editNodes from current route's OSRM waypoints if available,
  // otherwise from the route coordinates (pick evenly spaced points)
  if (currentRouteWaypoints && currentRouteWaypoints.length > 1) {
    // Use OSRM waypoints (skip the first one which is the start/location point)
    editNodes = currentRouteWaypoints.slice(1).map(wp => {
      const lat = wp.location[1];
      const lon = wp.location[0];
      return { lat, lon, marker: null };
    });
  } else if (currentRouteCoords && currentRouteCoords.length > 2) {
    // For imported/shared routes: sample 3-4 evenly spaced points
    const numToPick = Math.min(4, Math.max(2, Math.floor(currentRouteCoords.length / 50)));
    editNodes = [];
    for (let i = 1; i <= numToPick; i++) {
      const idx = Math.floor((i / (numToPick + 1)) * currentRouteCoords.length);
      editNodes.push({
        lat: currentRouteCoords[idx][0],
        lon: currentRouteCoords[idx][1],
        marker: null,
      });
    }
  }

  // Draw node markers
  drawEditNodes();

  // Show toolbar
  const $toolbar = document.getElementById('edit-toolbar');
  if ($toolbar) $toolbar.style.display = 'flex';
  updateUndoButton();
}

/**
 * Exit edit mode: remove node markers, hide toolbar.
 */
function exitEditMode() {
  isEditing = false;
  editNodes.forEach(n => n.marker?.remove());
  editNodes = [];
  editHistory = [];
  hasUnsavedEdits = false;

  if (previewLayer) { previewLayer.remove(); previewLayer = null; }
  if (ghostMarker) { ghostMarker.remove(); ghostMarker = null; }

  const $toolbar = document.getElementById('edit-toolbar');
  if ($toolbar) $toolbar.style.display = 'none';
}

/**
 * Draw amber circle markers for all edit nodes.
 * Each marker is draggable and has right-click for delete.
 */
function drawEditNodes() {
  for (const node of editNodes) {
    if (node.marker) node.marker.remove();

    const m = L.marker([node.lat, node.lon], {
      draggable: true,
      icon: L.divIcon({
        className: 'edit-node-marker',
        html: '<div class="edit-node-dot"></div>',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      }),
    }).addTo(map);

    // Prevent map drag when interacting with the marker
    m.on('mousedown', (e) => {
      L.DomEvent.stopPropagation(e);
    });
    m.on('touchstart', (e) => {
      L.DomEvent.stopPropagation(e);
    });

    // Drag: show live preview, then re-route on release
    m.on('drag', () => {
      const pos = m.getLatLng();
      node.lat = pos.lat;
      node.lon = pos.lng;
      showDragPreview();
    });

    m.on('dragend', () => {
      clearPreview();
      pushEditHistory();
      reRouteThroughNodes();
    });

    // Right-click: delete node
    m.on('contextmenu', (e) => {
      e.originalEvent.preventDefault();
      pushEditHistory();
      editNodes = editNodes.filter(n => n !== node);
      m.remove();
      if (editNodes.length >= 2) {
        reRouteThroughNodes();
      } else {
        // Not enough nodes to route — clear the route
        if (routeLayer) { routeLayer.remove(); routeLayer = null; }
        currentRouteCoords = null;
      }
      hasUnsavedEdits = true;
    });

    node.marker = m;
  }
}

/**
 * Show a live preview polyline during node drag.
 * Uses the current edit node positions (no OSRM call — just straight lines).
 */
function showDragPreview() {
  if (previewLayer) previewLayer.remove();

  const points = editNodes.map(n => [n.lat, n.lon]);

  // If we have current route coords, try to show a rough preview by
  // replacing the dragged node's position in the existing route.
  // Simple approach: just draw straight segments between nodes.
  previewLayer = L.polyline(points, {
    color: '#f59e0b',
    weight: 4,
    opacity: 0.5,
    dashArray: '8,6',
  }).addTo(map);
}

function clearPreview() {
  if (previewLayer) { previewLayer.remove(); previewLayer = null; }
}

/**
 * Re-route through the current edit nodes using OSRM.
 * Preserves the start point (currentLocation or first node).
 */
async function reRouteThroughNodes() {
  if (isReRouting) return;
  if (editNodes.length < 2) return;

  isReRouting = true;
  showStatus('Re-routing...');

  try {
    const profile = $profile?.value || 'foot';
    const profileParam = profile === 'bike' ? 'cycling' : 'foot';
    const customModel = buildCustomModel($optScenic?.checked);
    const fetchOptions = customModel
      ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(customModel) }
      : {};

    // Build coordinates: start point + edit nodes
    let coords;
    if (currentLocation) {
      coords = [
        `${currentLocation.lon},${currentLocation.lat}`,
        ...editNodes.map(n => `${n.lon},${n.lat}`),
      ].join(';');
    } else {
      // No fixed start — use first node as source
      coords = editNodes.map(n => `${n.lon},${n.lat}`).join(';');
    }

    const url = `${OSRM_URL}/trip/v1/${profileParam}/${coords}` +
      `?roundtrip=true&source=first&destination=any&geometries=geojson&overview=full`;

    const res = await fetch(url, fetchOptions);
    if (!res.ok) throw new Error('OSRM error');
    const data = await res.json();
    if (data.code !== 'Ok' || !data.trips?.length) throw new Error('No route');

    const trip = data.trips[0];
    let routeCoords = trip.geometry.coordinates.map(([lon, lat]) => [lat, lon]);
    routeCoords = trimDeadEnds(routeCoords);
    const distance = calculateRouteDistance(routeCoords);
    const turns = countTurns(routeCoords);

    // Snap edit nodes to their actual road positions from OSRM response
    if (data.waypoints) {
      for (let i = 0; i < data.waypoints.length && i < editNodes.length + 1; i++) {
        const wp = data.waypoints[i];
        // Skip the first waypoint if it's the start location
        const nodeIdx = currentLocation ? i - 1 : i;
        if (nodeIdx >= 0 && nodeIdx < editNodes.length && wp.location) {
          editNodes[nodeIdx].lat = wp.location[1];
          editNodes[nodeIdx].lon = wp.location[0];
          editNodes[nodeIdx].marker?.setLatLng([wp.location[1], wp.location[0]]);
        }
      }
    }

    // Redraw the route line
    if (routeLayer) routeLayer.remove();
    routeLayer = L.polyline(routeCoords, {
      color: '#e94560',
      weight: 5,
      opacity: 0.8,
    }).addTo(map);

    // Re-attach click handler for node insertion
    routeLayer.on('click', onRouteClick);

    currentRouteCoords = routeCoords;
    currentRouteWaypoints = data.waypoints;

    // Update route info
    const distKm = (distance / 1000).toFixed(2);
    const timeMin = Math.round((distance / 1000) / (profile === 'bike' ? 4.5 : 1.4) * 60);
    const timeStr = timeMin >= 60
      ? `${Math.floor(timeMin / 60)}h ${timeMin % 60}min`
      : `${timeMin} min`;

    $routeDistance.textContent = `${distKm} km`;
    $routeTime.textContent = timeStr;
    $routeTurns.textContent = turns === 0
      ? 'Straight loop — no turns!'
      : `${turns} turn${turns > 1 ? 's' : ''}`;

    hasUnsavedEdits = true;

  } catch (err) {
    showError(`Re-routing failed: ${err.message}`);
  } finally {
    isReRouting = false;
    hideStatus();
  }
}

/**
 * Find the nearest point on the current route polyline to a given latlng.
 * Returns the index in currentRouteCoords where the new node should be inserted.
 */
function findNearestSegmentIndex(lat, lon) {
  if (!currentRouteCoords || currentRouteCoords.length < 2) return 0;

  let bestDist = Infinity;
  let bestIdx = 0;

  for (let i = 0; i < currentRouteCoords.length - 1; i++) {
    const [lat1, lon1] = currentRouteCoords[i];
    const [lat2, lon2] = currentRouteCoords[i + 1];

    // Distance from point to segment (lat,lon) — (lat1,lon1)-(lat2,lon2)
    const dx = lon2 - lon1;
    const dy = lat2 - lat1;
    const lenSq = dx * dx + dy * dy;
    let t = 0;
    if (lenSq > 0) {
      t = Math.max(0, Math.min(1, ((lon - lon1) * dx + (lat - lat1) * dy) / lenSq));
    }
    const projLat = lat1 + t * dy;
    const projLon = lon1 + t * dx;
    const dLat = lat - projLat;
    const dLon = lon - projLon;
    const dist = dLat * dLat + dLon * dLon;

    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i + 1; // insert after this segment
    }
  }

  return bestIdx;
}

/**
 * Show a temporary "ghost" marker at the clicked position that fades out.
 */
function showGhostMarker(lat, lon) {
  if (ghostMarker) ghostMarker.remove();

  const ghost = L.circleMarker([lat, lon], {
    radius: 10,
    color: '#f59e0b',
    fillColor: '#f59e0b',
    fillOpacity: 0.3,
    weight: 1,
    opacity: 0.6,
  }).addTo(map);

  // Fade out
  let opacity = 0.6;
  const fadeInterval = setInterval(() => {
    opacity -= 0.1;
    if (opacity <= 0) {
      clearInterval(fadeInterval);
      ghost.remove();
      ghostMarker = null;
    } else {
      ghost.setStyle({ opacity, fillOpacity: opacity * 0.5 });
    }
  }, 100);

  ghostMarker = ghost;
}

/**
 * Handle click on the route line — insert a new edit node.
 */
function onRouteClick(e) {
  if (!isEditing) return;
  if (isReRouting) return;
  if (editNodes.length >= MAX_EDIT_NODES) {
    showStatus(`Max ${MAX_EDIT_NODES} nodes reached`, 'error');
    setTimeout(hideStatus, 2000);
    return;
  }

  const { lat, lng: lon } = e.latlng;

  // Show ghost at click position
  showGhostMarker(lat, lon);

  // Find where to insert in the node list
  // We add the new node to the end — OSRM /trip reorders optimally anyway
  pushEditHistory();

  editNodes.push({ lat, lon, marker: null });
  drawEditNodes();
  reRouteThroughNodes();
}

/**
 * Handle double-click on the map — add a new node.
 */
function onMapDblClick(e) {
  if (!isEditing) return;
  if (isReRouting) return;
  if (editNodes.length >= MAX_EDIT_NODES) {
    showStatus(`Max ${MAX_EDIT_NODES} nodes reached`, 'error');
    setTimeout(hideStatus, 2000);
    return;
  }

  const { lat, lng: lon } = e.latlng;

  showGhostMarker(lat, lon);

  pushEditHistory();

  editNodes.push({ lat, lon, marker: null });
  drawEditNodes();
  reRouteThroughNodes();
}

// ─── Route Generation (Main Entry Point) ───────────────────────────

/**
 * Build an OSRM custom model from the user's option selections.
 * This is sent as a JSON body with the /trip POST request, allowing
 * query-time priority adjustments without rebuilding the graph.
 *
 * OSRM custom model supports conditional priority/speed rules based on
 * OSM way properties. We use these to penalize or prefer certain edges.
 */
function buildCustomModel(wantScenic = false) {
  const priorities = [];

  // Surface type preference
  const surface = $optSurface.value;
  if (surface === 'paved') {
    priorities.push({ "if": "surface == unpaved || surface == gravel || surface == dirt || surface == ground || surface == grass || surface == sand", "value": 0.5 });
  } else if (surface === 'unpaved') {
    priorities.push({ "if": "surface == asphalt || surface == paved || surface == concrete", "value": 0.5 });
  }

  // Minimize turns — handled via multi-candidate scoring (client-side).
  // We still slightly prefer longer uninterrupted paths in the custom model
  // to help OSRM pick smoother segments within each candidate route.
  if ($optMinTurns.checked) {
    priorities.push({ "if": "highway == footway || highway == path || highway == track", "value": 1.15 });
    priorities.push({ "if": "highway == residential || highway == living_street", "value": 0.85 });
  }

  // Minimize crossings — penalize edges near pedestrian crossings
  if ($optMinCrossings.checked) {
    priorities.push({ "if": "crossing == yes || crossing == uncontrolled || crossing == zebra", "value": 0.5 });
    priorities.push({ "if": "highway == footway && footway != crossing", "value": 1.3 });
    priorities.push({ "if": "highway == pedestrian", "value": 1.3 });
  }

  // Minimize traffic lights — penalize edges with traffic signals
  if ($optMinTrafficLights.checked) {
    priorities.push({ "if": "crossing == signals || highway == traffic_signals", "value": 0.3 });
    priorities.push({ "if": "highway == footway || highway == path || highway == track", "value": 1.3 });
    priorities.push({ "if": "highway == pedestrian", "value": 1.3 });
  }

  // Avoid main roads — deprioritize primary/trunk/secondary highways
  if ($optAvoidMainRoads.checked) {
    priorities.push({ "if": "highway == trunk || highway == trunk_link", "value": 0.2 });
    priorities.push({ "if": "highway == primary || highway == primary_link", "value": 0.3 });
    priorities.push({ "if": "highway == secondary || highway == secondary_link", "value": 0.5 });
    priorities.push({ "if": "highway == tertiary || highway == tertiary_link", "value": 0.7 });
    priorities.push({ "if": "highway == footway || highway == path || highway == track", "value": 1.4 });
    priorities.push({ "if": "highway == pedestrian", "value": 1.4 });
    priorities.push({ "if": "highway == residential || highway == living_street", "value": 1.1 });
  }

  // Prefer lit paths — boost ways tagged with lit=yes
  if ($optPreferLit.checked) {
    priorities.push({ "if": "lit == yes", "value": 1.5 });
    priorities.push({ "if": "lit == no", "value": 0.5 });
  }

  // Scenic route preferences — boost footpaths/tracks/pedestrian, penalize main roads
  if (wantScenic) {
    priorities.push({ "if": "highway == footway && footway != crossing", "value": 1.5 });
    priorities.push({ "if": "highway == path", "value": 1.4 });
    priorities.push({ "if": "highway == pedestrian", "value": 1.3 });
    priorities.push({ "if": "highway == track", "value": 1.2 });
    priorities.push({ "if": "highway == primary || highway == primary_link", "value": 0.4 });
    priorities.push({ "if": "highway == secondary || highway == secondary_link", "value": 0.6 });
    // Boost edges through parks/water if tagged on the way
    priorities.push({ "if": "leisure == park || leisure == garden", "value": 1.6 });
    priorities.push({ "if": "natural == water || waterway != null", "value": 1.4 });
  }

  if (priorities.length === 0) return null;

  return {
    custom: {
      priority: priorities,
    }
  };
}

/**
 * Iteratively refine a polygon route to converge on the target distance.
 *
 * Instead of uniformly scaling the radius, we adjust individual waypoints
 * based on per-leg distance errors from OSRM. This converges faster and
 * more precisely because we target the actual source of error.
 *
 * OSRM /trip reorders waypoints for the optimal loop. We use waypoint_index
 * from the response to map legs back to our input waypoint indices.
 *
 * @returns { trip, osrmWaypoints, distance, turnCount, routeCoords } | null
 */
async function refinePolygonRoute(startLat, startLon, targetDistanceMeters,
                                  profileParam, fetchOptions, heading, numVertices) {
  const distanceKm = targetDistanceMeters / 1000;

  // Correction factor accounts for network snap overhead
  // (routed distance > geometric perimeter)
  const correctionFactor = distanceKm <= 3 ? 0.58 :
                           distanceKm <= 8 ? 0.68 :
                           distanceKm <= 15 ? 0.75 : 0.80;
  const baseRadius = (targetDistanceMeters / (2 * Math.PI)) * correctionFactor;

  // Relax tolerance for long routes where sparse roads make convergence harder
  const tolerance = distanceKm > 15 ? 0.15 : 0.10;
  const maxIterations = 5;
  const expectedLegDistance = targetDistanceMeters / (numVertices + 1);

  // Per-vertex scale factors (refined during iteration)
  let shapeVariation = new Array(numVertices).fill(1.0);

  let bestResult = null;
  let bestError = Infinity;

  for (let iter = 0; iter < maxIterations; iter++) {
    // Generate polygon waypoints with current shape variation
    const waypoints = generatePolygonWaypoints(
      startLat, startLon, baseRadius, numVertices, heading, shapeVariation
    );

    // Build OSRM /trip URL
    const coords = [
      `${startLon},${startLat}`,
      ...waypoints.map(wp => `${wp.lon},${wp.lat}`),
    ].join(';');

    const url = `${OSRM_URL}/trip/v1/${profileParam}/${coords}` +
      `?roundtrip=true&source=first&destination=any&geometries=geojson&overview=full`;

    try {
      const res = await fetch(url, fetchOptions);
      if (!res.ok) continue;
      const data = await res.json();
      if (data.code !== 'Ok' || !data.trips?.length) continue;

      const trip = data.trips[0];
      const routeCoords = trip.geometry.coordinates.map(([lon, lat]) => [lat, lon]);
      const trimmed = trimDeadEnds(routeCoords);
      const actualDistance = calculateRouteDistance(trimmed);
      const absError = Math.abs(actualDistance - targetDistanceMeters) / targetDistanceMeters;

      // Track best result across all iterations
      if (absError < bestError) {
        bestError = absError;
        bestResult = {
          trip,
          osrmWaypoints: data.waypoints,
          distance: actualDistance,
          turnCount: countTurns(trimmed),
          routeCoords: trimmed,
        };
      }

      // Check convergence
      if (absError <= tolerance) break;

      // Per-waypoint adjustment using leg distances
      // OSRM reorders waypoints — map trip order back to input indices
      if (trip.legs && trip.legs.length > 0 && data.waypoints) {
        // Build trip order: sort waypoints by their visit index in the trip
        // data.waypoints[i] corresponds to input coordinate i (0 = start)
        // waypoint_index tells us the visit position in the trip
        const tripOrder = data.waypoints
          .map((wp, i) => ({ inputIndex: i, tripIndex: wp.waypoint_index }))
          .sort((a, b) => a.tripIndex - b.tripIndex);

        // Leg i goes from trip position i to trip position (i+1)
        // The destination of leg i is tripOrder[(i+1) % (numVertices+1)]
        // If destination is position 0, that's the start — no waypoint to adjust
        for (let i = 0; i < trip.legs.length; i++) {
          const destTripIdx = (i + 1) % (numVertices + 1);
          if (destTripIdx === 0) continue; // returns to start, skip

          const wpInputIdx = tripOrder[destTripIdx].inputIndex - 1; // -1 because input[0] is start
          if (wpInputIdx < 0 || wpInputIdx >= numVertices) continue;

          const legDist = trip.legs[i].distance;
          const legError = (legDist - expectedLegDistance) / expectedLegDistance;

          // Damped adjustment: shrink waypoints on long legs, grow on short legs
          shapeVariation[wpInputIdx] *= (1 - legError * 0.5);
          shapeVariation[wpInputIdx] = Math.max(0.3, Math.min(2.5, shapeVariation[wpInputIdx]));
        }
      }

      // Global distance scaling: if total route is too short/long, scale
      // all waypoints outward/inward. This complements per-leg equalization
      // by directly targeting the total distance — the per-leg loop alone
      // can get stuck when all legs are proportionally short.
      if (actualDistance > 0) {
        const totalRatio = targetDistanceMeters / actualDistance;
        // Damped global scale (0.6 exponent prevents oscillation)
        const globalScale = Math.pow(totalRatio, 0.6);
        shapeVariation = shapeVariation.map(s =>
          Math.max(0.3, Math.min(2.5, s * globalScale))
        );
      }

    } catch (err) {
      continue;
    }
  }

  return bestResult;
}

/**
 * Generate a circular running route.
 *
 * If "Prefer scenic routes" is enabled, uses Approach 2 (scenic hub orienteering):
 * queries Overpass API for parks/water/forests near the start point, selects
 * scenic hubs as waypoints, and routes through them. Falls back to Approach 1
 * (geometric polygon) if no scenic nodes are found or Overpass is unavailable.
 *
 * Otherwise, uses Approach 1 directly: generates 3-4 polygon waypoints,
 * routes through them via OSRM /trip, and iteratively refines per-waypoint.
 *
 * When "minimize turns" is enabled, generates multiple candidates with
 * different headings/vertex counts/hub combos and picks the best.
 */
async function generateRoute() {
  if (!currentLocation) return;

  // Exit previous edit mode
  exitEditMode();

  const distanceKm = parseFloat($distance.value);
  const profile = $profile.value;
  const targetDistanceMeters = Math.round(distanceKm * 1000);
  const profileParam = profile === 'bike' ? 'cycling' : 'foot';
  const wantMinTurns = $optMinTurns.checked;
  const wantScenic = $optScenic?.checked;

  // Build custom model from user options
  const customModel = buildCustomModel(wantScenic);
  const fetchOptions = customModel
    ? {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customModel),
      }
    : {};

  let bestResult = null;
  let scenicHubs = null;
  let usedFallback = false;

  if (wantScenic) {
    // Try Approach 2: scenic hub orienteering
    const scenic = await generateScenicRoute(
      currentLocation.lat, currentLocation.lon,
      targetDistanceMeters, profileParam, fetchOptions, wantMinTurns
    );

    if (scenic.result) {
      bestResult = scenic.result;
      scenicHubs = scenic.scenicHubs;
    } else {
      // Fallback to Approach 1
      usedFallback = true;
    }
  }

  if (!bestResult) {
    // Approach 1: geometric polygon route generation
    const numCandidates = wantMinTurns ? 4 : 1;
    const baseHeading = Math.random() * 2 * Math.PI;
    const vertexOptions = wantMinTurns ? [3, 4] : [distanceKm <= 4 ? 3 : 4];

    let bestScore = Infinity;
    const totalCandidates = numCandidates * vertexOptions.length;
    let candidateNum = 0;

    for (let c = 0; c < numCandidates; c++) {
      const heading = baseHeading + (c / numCandidates) * 2 * Math.PI;

      for (const numVertices of vertexOptions) {
        candidateNum++;
        showStatus(totalCandidates > 1
          ? `Finding best route (${candidateNum}/${totalCandidates})...`
          : 'Generating your route...');

        const result = await refinePolygonRoute(
          currentLocation.lat, currentLocation.lon,
          targetDistanceMeters, profileParam, fetchOptions,
          heading, numVertices
        );

        if (!result) continue;

        const rawDistError = (result.distance - targetDistanceMeters) / targetDistanceMeters;
        const distError = Math.abs(rawDistError);
        if (distError > 0.4) continue;

        // Directional bias: penalize short routes more than long ones.
        // Runners prefer slightly-long over slightly-short (5.2km > 4.8km).
        const directionalError = rawDistError < 0
          ? distError * 1.5   // being short is worse
          : distError * 0.8;  // being long is more acceptable

        const score = wantMinTurns
          ? result.turnCount * 10 + directionalError * 100
          : directionalError * 100 + result.turnCount * 0.01;

        if (score < bestScore) {
          bestScore = score;
          bestResult = result;
        }
      }
    }
  }

  if (bestResult) {
    displayRoute(bestResult.trip, bestResult.osrmWaypoints, bestResult.distance, bestResult.turnCount);

    // Show scenic spots info if available
    const $scenicRow = document.getElementById('route-scenic-row');
    const $routeScenic = document.getElementById('route-scenic');
    if (scenicHubs && scenicHubs.length > 0) {
      const names = scenicHubs.map(h => h.name || h.type).filter(Boolean);
      if ($routeScenic) {
        $routeScenic.textContent = names.length > 0
          ? `${scenicHubs.length} — ${names.slice(0, 3).join(', ')}`
          : `${scenicHubs.length} scenic spots`;
      }
      if ($scenicRow) $scenicRow.style.display = 'flex';
    } else {
      if ($scenicRow) $scenicRow.style.display = 'none';
    }

    if (usedFallback) {
      showStatus('Scenic spots not found nearby — showing standard route', 'success');
      setTimeout(() => hideStatus(), 4000);
    } else {
      hideStatus();
    }
  } else {
    showError('No route found. Try a different location or distance.');
  }
}

// ─── Route Display ──────────────────────────────────────────────────

/**
 * Detect and remove out-and-back (dead-end) segments from the route.
 *
 * A dead-end looks like: A → B → C → B → D
 * where the route goes out to C and comes back the same way.
 * We detect this by finding segments where coordinates repeat in
 * reverse order, then skip over the out-and-back portion.
 */
function trimDeadEnds(coords) {
  if (coords.length < 4) return coords;

  // Distance threshold in degrees — points closer than this are "the same"
  // ~0.0001 degrees ≈ ~11 meters
  const threshold = 0.0001;

  const result = [];
  let i = 0;

  while (i < coords.length) {
    result.push(coords[i]);

    // Look ahead: is there an out-and-back starting from this point?
    // i.e., we find coords[i] again at some future index j, and the
    // coordinates between i and j are a palindrome (reversed on return)
    let foundDeadEnd = false;

    for (let j = i + 2; j < coords.length; j++) {
      const dist = Math.abs(coords[j][0] - coords[i][0]) +
                   Math.abs(coords[j][1] - coords[i][1]);

      if (dist < threshold) {
        // Found a point that matches coords[i] — check if the segment
        // between i and j is a palindrome (out and back along same path)
        const segLen = j - i;
        let isPalindrome = true;

        // Check that the path from i to the midpoint equals the reverse
        // of the path from the midpoint to j
        for (let k = 1; k < segLen; k++) {
          const fwd = coords[i + k];
          const rev = coords[j - k];
          if (Math.abs(fwd[0] - rev[0]) > threshold ||
              Math.abs(fwd[1] - rev[1]) > threshold) {
            isPalindrome = false;
            break;
          }
        }

        if (isPalindrome && segLen >= 4) {
          // Skip the entire out-and-back: jump to j
          i = j;
          foundDeadEnd = true;
          break;
        }
      }
    }

    if (!foundDeadEnd) {
      i++;
    }
  }

  return result;
}

// ─── Export Functions ───────────────────────────────────────────────

/**
 * Generate a GPX 1.1 file from route coordinates.
 * GPX is the universal GPS format — works with Garmin, Strava, Komoot, OsmAnd, etc.
 */
function generateGPX(coords, name = 'Running in Circles route') {
  const now = new Date().toISOString();

  const trkpts = coords.map(([lat, lon]) =>
    `      <trkpt lat="${lat.toFixed(7)}" lon="${lon.toFixed(7)}" />`
  ).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Running in Circles"
     xmlns="http://www.topografix.com/GPX/1/1"
     xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
     xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
  <metadata>
    <name>${name}</name>
    <time>${now}</time>
  </metadata>
  <trk>
    <name>${name}</name>
    <trkseg>
${trkpts}
    </trkseg>
  </trk>
</gpx>`;
}

/**
 * Download a GPX file in the browser.
 */
function downloadGPX() {
  if (!currentRouteCoords) return;

  const gpx = generateGPX(currentRouteCoords);
  const blob = new Blob([gpx], { type: 'application/gpx+xml' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  const dateStr = new Date().toISOString().slice(0, 10);
  a.download = `running-in-circles-${dateStr}.gpx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Open the route in Google Maps directions.
 *
 * Google Maps doesn't support GPX import for navigation, and it recalculates
 * its own route between waypoints (max 10). We use the OSRM waypoint locations
 * (not every coordinate) so Google has a manageable number of stops.
 * The route between them may differ from our exact path, but it's a useful
 * approximation for quick navigation.
 */
function openInGoogleMaps() {
  if (!currentRouteWaypoints || !currentLocation) return;

  // Google Maps allows max 10 waypoints (excluding start/end)
  // We have up to 10 OSRM waypoints + start point. Use start + first 9 waypoints.
  const wpCoords = currentRouteWaypoints
    .map(wp => `${wp.location[1]},${wp.location[0]}`) // lat,lon
    .slice(0, 9);

  const start = `${currentLocation.lat},${currentLocation.lon}`;
  const allPoints = [start, ...wpCoords, start]; // round trip

  // Google Maps directions URL
  const url = `https://www.google.com/maps/dir/${allPoints.join('/')}`;

  window.open(url, '_blank');
}

// ─── Voice Guidance ─────────────────────────────────────────────────

/**
 * Calculate bearing between two GPS points (0-360°, 0 = north, clockwise).
 */
function bearing(lat1, lon1, lat2, lon2) {
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);

  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

/**
 * Haversine distance between two GPS points in meters.
 */
function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Extract turn points from route coordinates.
 *
 * Each turn has: position (lat, lon), direction ('left'/'right'),
 * angle (degrees), and a flag for whether it's been announced.
 *
 * Points closer than 5m are merged to reduce noise (same as countTurns).
 */
function extractTurns(coords, thresholdDeg = 25) {
  if (coords.length < 3) return [];

  // Merge points closer than 5m
  const merged = [coords[0]];
  for (let i = 1; i < coords.length; i++) {
    const prev = merged[merged.length - 1];
    if (haversine(prev[0], prev[1], coords[i][0], coords[i][1]) > 5) {
      merged.push(coords[i]);
    }
  }

  const turns = [];

  for (let i = 1; i < merged.length - 1; i++) {
    const [lat1, lon1] = merged[i - 1];
    const [lat2, lon2] = merged[i];
    const [lat3, lon3] = merged[i + 1];

    const bearingIn = bearing(lat1, lon1, lat2, lon2);
    const bearingOut = bearing(lat2, lon2, lat3, lon3);

    // Turn angle: difference normalized to -180..180
    let diff = ((bearingOut - bearingIn + 540) % 360) - 180;

    if (Math.abs(diff) > thresholdDeg) {
      turns.push({
        lat: lat2,
        lng: lon2,
        direction: diff > 0 ? 'right' : 'left',
        angle: Math.abs(diff),
        announced: false,
        routeIndex: i,
      });
    }
  }

  return turns;
}

/**
 * Speak a direction using the Web Speech API.
 * Cancels any pending speech to avoid queuing.
 */
function speak(text) {
  if (!window.speechSynthesis) return;

  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'en-US';
  utterance.rate = 1.1;
  utterance.volume = 1.0;
  window.speechSynthesis.speak(utterance);
}

/**
 * Request a screen wake lock to prevent the screen from turning off
 * while guidance is active (keeps GPS alive on mobile).
 */
async function requestWakeLock() {
  if (!navigator.wakeLock) return null;
  try {
    return await navigator.wakeLock.request('screen');
  } catch {
    return null;
  }
}

/**
 * Start voice guidance — begins GPS tracking and announces turns.
 */
async function startGuidance() {
  if (!currentRouteCoords || guidanceActive) return;

  if (!navigator.geolocation) {
    showError('Geolocation not supported by your browser');
    return;
  }

  // Extract turns from the route
  guidanceTurns = extractTurns(currentRouteCoords);
  guidanceTurns.forEach(t => t.announced = false);
  guidanceActive = true;

  // Request wake lock to keep screen on
  guidanceWakeLock = await requestWakeLock();

  // Update UI
  const $btn = document.getElementById('guidance-btn');
  $btn.textContent = '⏹ Stop Guidance';
  $btn.classList.add('active');
  showStatus('Searching for GPS signal...', 'loading');

  // Start watching position
  guidanceWatchId = navigator.geolocation.watchPosition(
    onGuidancePosition,
    onGuidanceError,
    { enableHighAccuracy: true, maximumAge: 1000, timeout: 8000 }
  );
}

/**
 * Stop voice guidance — cleans up GPS tracking and markers.
 */
function stopGuidance() {
  guidanceActive = false;

  if (guidanceWatchId !== null) {
    navigator.geolocation.clearWatch(guidanceWatchId);
    guidanceWatchId = null;
  }

  if (guidanceUserMarker) {
    map.removeLayer(guidanceUserMarker);
    guidanceUserMarker = null;
  }

  if (guidanceAccuracyCircle) {
    map.removeLayer(guidanceAccuracyCircle);
    guidanceAccuracyCircle = null;
  }

  if (guidanceWakeLock) {
    guidanceWakeLock.release();
    guidanceWakeLock = null;
  }

  if (window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }

  // Update UI
  const $btn = document.getElementById('guidance-btn');
  $btn.textContent = '🔊 Start Guidance';
  $btn.classList.remove('active');

  hideStatus();
}

/**
 * Handle GPS position updates during guidance.
 */
function onGuidancePosition(pos) {
  const { latitude, longitude, accuracy, speed } = pos.coords;
  guidanceLastSpeed = speed || 0;

  // Ignore very inaccurate fixes
  if (accuracy > 50) {
    showStatus(`GPS accuracy low (${Math.round(accuracy)}m). Waiting for better signal...`, 'loading');
    return;
  }

  // Update or create user marker
  if (guidanceUserMarker) {
    guidanceUserMarker.setLatLng([latitude, longitude]);
    guidanceAccuracyCircle.setLatLng([latitude, longitude]);
    guidanceAccuracyCircle.setRadius(accuracy);
  } else {
    guidanceUserMarker = L.circleMarker([latitude, longitude], {
      radius: 7,
      color: '#fff',
      fillColor: '#2ecc71',
      fillOpacity: 1,
      weight: 3,
    }).addTo(map);

    guidanceAccuracyCircle = L.circle([latitude, longitude], {
      radius: accuracy,
      color: '#2ecc71',
      fillColor: '#2ecc71',
      fillOpacity: 0.1,
      weight: 1,
    }).addTo(map);

    // Center map on user
    map.setView([latitude, longitude], 16);
  }

  // Check for upcoming turns
  // Announce distance scales with speed: walking 40m, running 60m, cycling 80m
  const announceDist = speed > 6 ? 80 : speed > 2.5 ? 60 : 40;

  let nearestUnannounced = null;
  let nearestDist = Infinity;

  for (const turn of guidanceTurns) {
    if (turn.announced) continue;

    const dist = haversine(latitude, longitude, turn.lat, turn.lng);

    if (dist < announceDist && dist < nearestDist) {
      nearestDist = dist;
      nearestUnannounced = turn;
    }
  }

  if (nearestUnannounced) {
    nearestUnannounced.announced = true;
    speak(nearestUnannounced.direction);
    showStatus(`Turn ${nearestUnannounced.direction}!`, 'success');
    setTimeout(() => {
      if (!guidanceActive) return;
      const remaining = guidanceTurns.filter(t => !t.announced).length;
      if (remaining > 0) {
        showStatus(`Tracking · ${remaining} turn${remaining > 1 ? 's' : ''} remaining`, 'loading');
      } else {
        showStatus('All turns passed — you should be back soon!', 'success');
      }
    }, 2000);
  } else {
    // Update status with remaining turns
    const remaining = guidanceTurns.filter(t => !t.announced).length;
    if (remaining > 0) {
      showStatus(`Tracking · ${remaining} turn${remaining > 1 ? 's' : ''} remaining`, 'loading');
    } else if (currentLocation) {
      // Check if near start point (route complete)
      const distToStart = haversine(latitude, longitude, currentLocation.lat, currentLocation.lon);
      if (distToStart < 30) {
        showStatus('Route complete! 🎉', 'success');
        speak('Route complete');
        stopGuidance();
      } else {
        showStatus('All turns passed — heading back to start', 'loading');
      }
    }
  }
}

/**
 * Handle GPS errors during guidance.
 */
function onGuidanceError(err) {
  let msg = 'GPS error';
  if (err.code === 1) msg = 'Location access denied. Check browser permissions.';
  else if (err.code === 2) msg = 'GPS signal unavailable. Try going outside.';
  else if (err.code === 3) msg = 'GPS request timed out. Retrying...';
  showStatus(msg, 'error');
}

// ─── Route Display ──────────────────────────────────────────────────

function displayRoute(trip, waypoints, precalcDistance, turnCount) {
  // Remove previous route and waypoint markers
  if (routeLayer) map.removeLayer(routeLayer);
  waypointMarkers.forEach(m => map.removeLayer(m));
  waypointMarkers = [];

  // Extract coordinates from GeoJSON
  let coords = trip.geometry.coordinates.map(([lon, lat]) => [lat, lon]);

  // Trim dead-end out-and-back segments
  coords = trimDeadEnds(coords);

  // Store for export functions
  currentRouteCoords = coords;
  currentRouteWaypoints = waypoints;

  // Use pre-calculated distance (already accounts for trimming) if provided,
  // otherwise recalculate
  const actualDistance = precalcDistance ?? calculateRouteDistance(coords);

  // Draw the route
  routeLayer = L.polyline(coords, {
    color: '#e94560',
    weight: 5,
    opacity: 0.8,
  }).addTo(map);

  // Click on route line: insert a new node
  routeLayer.on('click', onRouteClick);

  // Draw waypoint markers (optional, shows the circle)
  if (waypoints) {
    for (const wp of waypoints) {
      const [lon, lat] = [wp.location[0], wp.location[1]];
      const m = L.circleMarker([lat, lon], {
        radius: 4,
        color: '#0f3460',
        fillColor: '#8ecae6',
        fillOpacity: 0.8,
        weight: 2,
      }).addTo(map);
      // Click on waypoint marker also triggers node insertion
      m.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        onRouteClick(e);
      });
      waypointMarkers.push(m);
    }
  }

  // Fit map to show entire route
  map.fitBounds(routeLayer.getBounds(), { padding: [40, 40] });

  // Show route info (use recalculated distance after trimming)
  const distanceKm = (actualDistance / 1000).toFixed(2);
  const timeMin = Math.round((actualDistance / 1000) / (profile === 'bike' ? 4.5 : 1.4) * 60);
  const timeStr = timeMin >= 60
    ? `${Math.floor(timeMin / 60)}h ${timeMin % 60}min`
    : `${timeMin} min`;

  $routeDistance.textContent = `${distanceKm} km`;
  $routeTime.textContent = timeStr;
  $routePoints.textContent = `${currentLocation.label?.split(',')[0] || 'Same point'}`;

  // Show turn count (calculate if not provided)
  const turns = turnCount ?? countTurns(coords);
  $routeTurns.textContent = turns === 0 ? 'Straight loop — no turns!' : `${turns} turn${turns > 1 ? 's' : ''}`;

  $routeInfo.classList.add('visible');

  // Show export buttons + info buttons
  const $exportGpx = document.getElementById('export-gpx');
  const $exportGmaps = document.getElementById('export-gmaps');
  const $infoGpx = document.getElementById('info-gpx');
  const $infoGmaps = document.getElementById('info-gmaps');
  if ($exportGpx) $exportGpx.style.display = 'flex';
  if ($exportGmaps) $exportGmaps.style.display = 'flex';
  if ($infoGpx) $infoGpx.style.display = 'flex';
  if ($infoGmaps) $infoGmaps.style.display = 'flex';

  // Show guidance, save, and share buttons
  const $guidanceBtn = document.getElementById('guidance-btn');
  const $saveBtn = document.getElementById('save-route-btn');
  const $shareBtn = document.getElementById('share-route-btn');
  if ($guidanceBtn) $guidanceBtn.style.display = 'flex';
  if ($saveBtn) $saveBtn.style.display = 'flex';
  if ($shareBtn) $shareBtn.style.display = 'flex';

  // Set route name for generated routes
  currentRouteName = `${distanceKm} km loop`;

  // Enter edit mode
  enterEditMode();

  // Show success briefly
  const turnLabel = turns === 0 ? 'no turns' : `${turns} turn${turns > 1 ? 's' : ''}`;
  showStatus(`Route generated! ${distanceKm} km loop · ${turnLabel}`, 'success');
  setTimeout(() => hideStatus(), 3000);
}

// ─── Modals ─────────────────────────────────────────────────────────

function openModal(id) {
  document.getElementById(id)?.classList.add('visible');
}

function closeModal(id) {
  document.getElementById(id)?.classList.remove('visible');
}

// ─── Event Listeners ────────────────────────────────────────────────

$geocodeBtn.addEventListener('click', () => {
  const addr = $address.value.trim();
  if (addr) geocodeAddress(addr);
});

$address.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') {
    const addr = $address.value.trim();
    if (addr) geocodeAddress(addr);
  }
});

$gpsBtn.addEventListener('click', useDeviceLocation);

$distance.addEventListener('input', () => {
  $distanceValue.textContent = `${$distance.value} km`;
});

// Collapsible options toggle
$optionsHeader.addEventListener('click', () => {
  $optionsHeader.classList.toggle('open');
  $optionsPanel.classList.toggle('open');
});

$generateBtn.addEventListener('click', () => {
  generateRoute();
});

// Export buttons
document.getElementById('export-gpx')?.addEventListener('click', downloadGPX);
document.getElementById('export-gmaps')?.addEventListener('click', openInGoogleMaps);

// Voice guidance button
document.getElementById('guidance-btn')?.addEventListener('click', () => {
  if (guidanceActive) stopGuidance();
  else startGuidance();
});

// Save route button
document.getElementById('save-route-btn')?.addEventListener('click', saveCurrentRoute);

// Edit undo button
document.getElementById('edit-undo-btn')?.addEventListener('click', undoLastEdit);

// Share route button
document.getElementById('share-route-btn')?.addEventListener('click', openShareModal);

// Import GPX button + hidden file input
document.getElementById('import-gpx-btn')?.addEventListener('click', () => {
  document.getElementById('import-gpx-file')?.click();
});
document.getElementById('import-gpx-file')?.addEventListener('change', (e) => {
  const file = e.target.files?.[0];
  if (file) handleGPXImport(file);
  e.target.value = ''; // reset so same file can be re-imported
});

// Share modal: copy link button
document.getElementById('copy-link-btn')?.addEventListener('click', () => {
  const $input = document.getElementById('share-link-input');
  if ($input?.value) {
    navigator.clipboard.writeText($input.value).then(() => {
      const $btn = document.getElementById('copy-link-btn');
      const origText = $btn.textContent;
      $btn.textContent = '✓ Copied!';
      setTimeout(() => { $btn.textContent = origText; }, 2000);
    }).catch(() => {
      $input.select();
      document.execCommand('copy');
    });
  }
});

// Share modal: download GPX
document.getElementById('modal-download-gpx')?.addEventListener('click', () => {
  downloadGPX();
});

// Info buttons → open modals
document.getElementById('info-gpx')?.addEventListener('click', () => openModal('modal-gpx'));
document.getElementById('info-gmaps')?.addEventListener('click', () => openModal('modal-gmaps'));

// Modal close handlers
document.querySelectorAll('.modal-close').forEach(btn => {
  btn.addEventListener('click', () => closeModal(btn.dataset.close));
});
document.querySelectorAll('.modal-overlay').forEach(overlay => {
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeModal(overlay.id);
  });
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    document.querySelectorAll('.modal-overlay.visible').forEach(m => closeModal(m.id));
  }
});

// ─── Init ───────────────────────────────────────────────────────────

initMap();
renderSavedRoutes();

// Check URL for shared route on page load
const sharedRoute = parseShareLinkFromURL();
if (sharedRoute) {
  currentRouteName = sharedRoute.name;
  // Wait for map to be ready, then display
  setTimeout(() => {
    displayImportedRoute(sharedRoute.coords, sharedRoute.name, calculateRouteDistance(sharedRoute.coords));
    showStatus(`Shared route: ${sharedRoute.name}`, 'success');
    setTimeout(() => hideStatus(), 4000);
  }, 200);
}

// Warn when leaving page with unsaved edits
window.addEventListener('beforeunload', (e) => {
  if (hasUnsavedEdits) {
    e.preventDefault();
    e.returnValue = '';
  }
});
